/** Valor de business_detail.business_type que identifica una orden ODD. */
export const ODD_BUSINESS_TYPE = 'odd';

/** Valor por defecto de business_detail.business_type: las órdenes sin tipo son tradicionales. */
export const TRADITIONAL_BUSINESS_TYPE = 'tradicional';

/** Base de la app de Siloreal, para enlazar una silobolsa por su `silo_id`. */
export const SILOREAL_APP_URL = 'https://odd.agrology.com/';

/**
 * Snapshot de una silobolsa comprometida, tal como se persiste en `negotiation_silobags` y
 * viaja en `negotiation.silo_bags`: permite a la contraparte verlas sin llamar a Siloreal.
 */
export interface SiloBagRef {
  silobag_id: string;
  silobag_uuid?: string | null;
  silobag_name?: string | null;
  species?: string | null;
  harvest?: string | null;
  organization?: string | null;
  input_weight?: number | string | null;
  remaining_stock?: number | string | null;
  lot_name?: string | null;
  countryside_name?: string | null;
  humidity?: number | string | null;
  bagged_date?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
}

/**
 * De dónde viene la propiedad de la silobolsa (AGY-3883):
 * - `siloreal`: regla implícita, su organización de Siloreal está vinculada a la empresa.
 * - `organization`: la key de la empresa la ve, pero su organización es de otra empresa
 *   (`owner_company_*`) o no está vinculada a ninguna (owner en null).
 * - `negotiation`: adquirida en un negocio ODD anotado.
 * - `backoffice`: asignada desde backoffice.
 */
export type SiloBagOwnershipSource = 'siloreal' | 'organization' | 'negotiation' | 'backoffice';

export interface SiloBagOwnership {
  source: SiloBagOwnershipSource;
  /** Sólo cuando la propiedad no es la implícita. */
  owner_company_id?: number | null;
  owner_company_name?: string | null;
  /** Empresa en cuya cuenta de Siloreal vive la bolsa. */
  host_company_id?: number | null;
  /** Negocio anotado en el que se adquirió (source = negotiation). */
  source_negotiation_reference?: string | null;
  /** Fecha del último cambio de propiedad (ISO 8601). */
  since?: string | null;
}

/** Quién tiene comprometida una silobolsa. Sólo llega con `include_committed=1` (Monitoreo de Silobolsas). */
export type SiloBagCommitment = 'order' | 'negotiation';

export interface SiloBagCommitmentInfo {
  /** PK interna de la orden o de la negociación, según `SiloBag.committedBy`. */
  id: number;
  /** Número público: es el que esperan las rutas del front y la API, no la PK. */
  reference?: string | null;
}

/**
 * Silobolsa individual registrada en el campo del productor, construida desde el proxy
 * backend (`GET /companies/:id/siloreal/silobags`). Produce el snapshot (`toRef`) de la negociación.
 */
export class SiloBag {
  /** Identificador único de la silobolsa (SiloID de Siloreal). */
  silo_id: string;
  /** Tipo de grano almacenado actualmente en la bolsa (ej. soja, maiz, trigo). */
  grain_type: string;
  /** Peso en toneladas actualmente embolsado. */
  bagged_weight: number;
  /** Humedad (%) medida en el momento del embolsado. */
  humidity?: number | null;
  /** Fecha en que se selló la bolsa (YYYY-MM-DD). */
  bagged_date?: string | null;
  /** Días transcurridos desde que se selló la bolsa. */
  days_since_bagging: number;
  /** Geolocalización — latitud. */
  latitude?: number | null;
  /** Geolocalización — longitud. */
  longitude?: number | null;
  /**
   * ID de la orden a la que ya está comprometida esta bolsa.
   * Null significa que la bolsa está libre y puede seleccionarse para una nueva orden.
   */
  assigned_order_id?: number | null;
  /** Nombre de la bolsa en Siloreal (ej. "Bs 5"). */
  silobag_name?: string | null;
  /** Nombre del lote donde está ubicada la bolsa. */
  lot_name?: string | null;
  /** Nombre del campo/establecimiento. */
  countryside_name?: string | null;
  /**
   * Localidad y provincia del campo, por reverse geocoding en el backend (una vez por campo).
   * Sólo llegan con `with_location=1` (Monitoreo de Silobolsas); no van al snapshot.
   */
  locality?: string | null;
  province?: string | null;

  // Campos del snapshot que no se muestran en la tabla pero viajan al backend.
  /** UUID de la silobolsa en Siloreal. */
  silobag_uuid?: string | null;
  /** Cosecha / campaña. */
  harvest?: string | null;
  /** Organización de Siloreal dueña de la bolsa. */
  organization?: string | null;
  /** Id de la organización de Siloreal: define la propietaria implícita (AGY-3883). */
  organizationId?: string | null;
  /** Peso embolsado de origen. */
  input_weight?: number | null;
  /** Stock remanente (kg, según Siloreal). */
  remaining_stock?: number | null;

  // Campos usados por Monitoreo de Silobolsas (confirmados en el Swagger real de
  // Siloreal, "IOF API"). El selector de silobolsas los ignora.
  /** Estado de la bolsa según Siloreal (`silobag_data_statuses.name`). */
  status?: string | null;
  active?: boolean | null;
  partiallyExtracted?: boolean | null;
  extractedAll?: boolean | null;
  /** Total extraído acumulado (kg). */
  totalExtracted?: number | null;
  /** Texto corto de la última alarma activa, o null si no tiene. */
  alert?: string | null;
  /** Fecha real de cierre de la bolsa (sólo si ya cerró; no es una proyección). */
  closingDate?: string | null;
  /** true si la silobolsa tiene seguro SiloSeguro activo (tag INSURANCE_CREATED de Siloreal). */
  insured?: boolean;
  /** Resultado de la verificación VRR (tags VRR_RED/VRR_GREEN de Siloreal); null si aún no se calculó. */
  vrr?: 'ok' | 'alert' | null;
  committed?: boolean;
  committedBy?: SiloBagCommitment | null;
  commitment?: SiloBagCommitmentInfo | null;

  // Propiedad (AGY-3883).
  /** true si la bolsa es de la empresa que consulta (filtro "Mis silobolsas"). */
  owned?: boolean;
  ownership?: SiloBagOwnership | null;
  /** `snapshot`: Siloreal no respondió y los datos son los guardados al adquirirla. */
  dataSource?: 'live' | 'snapshot';
  /** Backoffice: la propietaria actual la tiene comprometida en un negocio activo (no se puede reasignar). */
  assignBlocked?: boolean;

  constructor(data: Partial<SiloBag> = {}) {
    Object.assign(this, data);
  }

  get hasAlert(): boolean {
    return !!this.alert;
  }

  /** Adquirida en ODD o asignada por backoffice: no vive en la cuenta propia de Siloreal. */
  get isAcquired(): boolean {
    return this.owned === true && !!this.ownership
      && this.ownership.source !== 'siloreal' && this.ownership.source !== 'organization';
  }

  /** Se ve en la cuenta propia de Siloreal pero se transfirió a otra empresa (venta ODD, backoffice). */
  get isTransferredAway(): boolean {
    return this.owned === false && this.ownership?.source !== 'organization';
  }

  /** La key de la empresa la ve, pero es de otra organización de Siloreal (o de una sin vincular). */
  get isThirdParty(): boolean {
    return this.owned === false && this.ownership?.source === 'organization';
  }

  /** Se puede comprometer: no está comprometida y es propia. */
  get isAvailable(): boolean {
    return !this.committed && this.owned !== false;
  }

  /**
   * Construye un SiloBag desde un item normalizado del proxy backend. Único lugar de mapeo
   * crudo→modelo, compartido por `SiloBagService` y `SiloMonitoringService`.
   */
  static fromApi(item: any): SiloBag {
    const baggedDate = SiloBag.toDateString(item.bagged_date ?? null);
    const committed = item.committed === true;

    return new SiloBag({
      silo_id:            String(item.silobag_id ?? ''),
      silobag_uuid:       item.silobag_uuid ?? null,
      silobag_name:       item.silobag_name ?? null,
      grain_type:         (item.species ?? '').toString().toLowerCase(),
      lot_name:           item.lot_name ?? null,
      countryside_name:   item.countryside_name ?? null,
      locality:           item.locality ?? null,
      province:           item.province ?? null,
      // Siloreal expresa stock en kg; la tabla muestra toneladas.
      bagged_weight:      SiloBag.toTons(item.remaining_stock ?? item.input_weight ?? null),
      remaining_stock:    SiloBag.toFiniteNumber(item.remaining_stock ?? null),
      input_weight:       SiloBag.toFiniteNumber(item.input_weight ?? null),
      humidity:           SiloBag.toFiniteNumber(item.humidity ?? null),
      bagged_date:        baggedDate,
      days_since_bagging: baggedDate ? SiloBag.daysSince(baggedDate) : 0,
      latitude:           SiloBag.toFiniteNumber(item.latitude ?? null),
      longitude:          SiloBag.toFiniteNumber(item.longitude ?? null),
      harvest:            item.harvest ?? null,
      organization:       item.organization ?? null,
      organizationId:     item.organization_id != null ? String(item.organization_id) : null,
      status:             item.status ?? null,
      active:             item.active ?? null,
      partiallyExtracted: item.partially_extracted ?? null,
      extractedAll:       item.extracted_all ?? null,
      totalExtracted:     SiloBag.toFiniteNumber(item.total_extracted ?? null),
      alert:              item.alert ?? null,
      closingDate:        SiloBag.toDateString(item.closing_date ?? null),
      insured:            item.insured === true,
      vrr:                item.vrr === 'ok' || item.vrr === 'alert' ? item.vrr : null,
      committed:          committed,
      committedBy:        committed ? (item.committed_by ?? null) : null,
      commitment:         committed ? (item.commitment ?? null) : null,
      // Sólo cuando la comprometió una ORDEN: en una negociación el id es de la negociación.
      assigned_order_id:  committed && item.committed_by === 'order'
        ? (item.commitment?.id ?? null)
        : null,
      // Un backend sin el campo es uno anterior a la propiedad: todo lo listado es propio.
      owned:              item.owned !== false,
      ownership:          item.ownership ?? null,
      dataSource:         item.data_source === 'snapshot' ? 'snapshot' : 'live',
      assignBlocked:      item.assign_blocked === true,
    });
  }

  private static toTons(raw: any): number {
    const parsed = SiloBag.toFiniteNumber(raw);
    return parsed != null ? parsed / 1000 : 0;
  }

  private static toFiniteNumber(raw: any): number | null {
    if (raw == null || raw === '') return null;
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private static toDateString(raw: string | null): string | null {
    if (!raw) return null;
    return raw.slice(0, 10);
  }

  private static daysSince(dateStr: string): number {
    const [year, month, day] = dateStr.split('-').map(Number);
    if (!year || !month || !day) return 0;
    const ms = Date.now() - new Date(year, month - 1, day).getTime();
    return Math.floor(ms / 86_400_000);
  }

  /**
   * Snapshot que se envía al backend en `negotiation.silo_bags`. El backend lo persiste
   * para que la contraparte pueda ver las bolsas comprometidas sin llamar a Siloreal.
   */
  toRef(): SiloBagRef {
    return {
      silobag_id: this.silo_id,
      silobag_uuid: this.silobag_uuid ?? null,
      silobag_name: this.silobag_name ?? null,
      species: this.grain_type ?? null,
      harvest: this.harvest ?? null,
      organization: this.organization ?? null,
      input_weight: this.input_weight ?? null,
      remaining_stock: this.remaining_stock ?? null,
      lot_name: this.lot_name ?? null,
      countryside_name: this.countryside_name ?? null,
      humidity: this.humidity ?? null,
      bagged_date: this.bagged_date ?? null,
      latitude: this.latitude ?? null,
      longitude: this.longitude ?? null,
    };
  }
}

/**
 * Evento de trazabilidad de una silobolsa, tal como lo devuelve
 * `GET /companies/:companyId/siloreal/silobags/:silobagId/history`.
 *
 * Cada evento registra un movimiento del compromiso de la bolsa: cuándo se comprometió,
 * cuándo se liberó y por qué, y qué negocio (orden / negociación / propuesta de canje) lo
 * originó. `user` en null significa que el actor no fue humano (cron, backfill, cascada).
 */
export interface SiloBagCommitmentEvent {
  /** Fecha/hora ISO del evento. */
  date: string;
  /** Qué le pasó a la bolsa (`transferred` = cambió de propietaria). */
  action: 'committed' | 'released' | 'resynced' | 'transferred';
  /** Motivo del evento dentro de la taxonomía del backend (ej. 'negotiation_cascade_rejected'). */
  reason: string | null;
  /** Origen del movimiento. */
  source: 'order' | 'negotiation' | 'barter' | 'backfill' | 'backoffice';
  /** Usuario que lo provocó; null = actor no humano (sistema). */
  user: string | null;
  /** Empresa que lo provocó. */
  company: string | null;
  order_id: number | null;
  order_reference: string | null;
  negotiation_id: number | null;
  negotiation_reference: string | null;
  barter_proposal_id: number | null;
  /**
   * Payload extra del backend. En `transferred` trae `{from, to, host}` (ids) y
   * `from_name`/`to_name`; para el resto el front no asume su forma.
   */
  details: { [key: string]: any } | null;
}
